import Product from '@models/product';
import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Image from '@repo/ui/src/components/Image/New';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { classNames } from '@repo/ui/src/utils/classNames';
import { VariationGroup } from '@typings/product';
import React from 'react';

export interface _TierTwoVariationsProps {
  product: Product;
}

const TierTwoVariations = ({ product }: _TierTwoVariationsProps) => {
  const { push } = useCurrentUrl();
  const { t } = useTranslation();
  const [disabled, setDisabled] = React.useState(false);

  const categoryVariationGroups = React.useMemo(() => {
    const categoryVariationsTypes = product.variationAttributes?.find(
      (item) => item.id === VariationGroup.category,
    )?.values;

    return (
      categoryVariationsTypes
        ?.map((category) => {
          return {
            value: category.value,
            variations: product.variations.filter(
              (variation) =>
                variation.variationValues?.find(({ name }) => name === VariationGroup.category)
                  ?.value === category.value,
            ),
          };
        })
        .filter(({ variations }) => variations.length) ?? []
    );
  }, [product]);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setDisabled(true);
    push(e.target.value);
  };

  if (!categoryVariationGroups.length) {
    return null;
  }

  return (
    <>
      <div className="md:hidden grid gap-y-2">
        <h4 className="tra-text-web-utl-link">
          {t('productDetailPageContent.tierTwoSelectLabel', undefined, {
            default: 'Select an option',
          })}
        </h4>
        <div className="relative grid after:block after:absolute after:bg-dropdown after:bg-cover after:inset-y-4 after:right-4 after:w-6 after:pointer-events-none">
          <select
            onChange={handleSelect}
            className="bg-white border-2 border-neutral-300 px-4 py-3 w-full disabled:text-neutral-300"
            disabled={disabled}
          >
            {categoryVariationGroups.map(({ value, variations }) => (
              <optgroup label={value} key={value}>
                {variations.map((variation) => (
                  <option
                    key={variation.sku}
                    value={variation.productUrl}
                    selected={variation.sku === product.sku}
                  >
                    {variation.title}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>
      <Accordion
        preExpanded={categoryVariationGroups.map((_, i) => i)}
        allowMultipleExpanded
        allowZeroExpanded
        className="hidden md:block"
      >
        {categoryVariationGroups.map(({ value, variations }, index) => (
          <AccordionItem
            key={index}
            uuid={index}
            heading={value}
            headerFontClass="tra-text-web-utl-link"
            headerClassName="!p-0"
          >
            <ul className="list-none flex flex-wrap gap-y-4 gap-x-6 px-0">
              {variations.map((variation) => (
                <li key={variation.title}>
                  <Anchor
                    link={variation.productUrl}
                    className={classNames(
                      'grid p-3 rounded relative group/item bg-white',
                      'after:absolute after:-inset-0 after:transition after:duration-300 after:ease-in-out after:bg-gradient-to-br after:from-transparent after:via-transparent after:to-warm-300 after:opacity-0',
                      // hover
                      'hover:after:opacity-100',
                      variation.sku === product.sku && 'border-brand-300',
                      variation.sku !== product.sku ? 'border-2 border-neutral-300' : 'border-4',
                    )}
                  >
                    <div className="relative min-w-20 h-20">
                      <Image
                        src={buildImageSrc(undefined, 80, 1, 1, variation.mainImageUrl)}
                        alt={variation.title}
                        fill
                        sizes="80px"
                        className="object-contain z-20"
                      />
                    </div>
                    <span className="grid justify-items-center absolute top-full transition-opacity opacity-0 group-hover/item:opacity-100 tra-text-web-body-l text-white bg-transparent-light-500 text-nowrap p-2.5 justify-self-center z-30 rounded pointer-events-none before:absolute before:bottom-full before:border-8 before:border-b-transparent-light-500 before:border-transparent before:justify-self-center">
                      {variation.title}
                    </span>
                  </Anchor>
                </li>
              ))}
            </ul>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
};

export default TierTwoVariations;
